/**
 * Production Tools Overview Page
 *
 * Hub page displaying all 5 production tools with navigation cards.
 * Tool colors match Next.js exactly:
 * - Auto Test: blue
 * - Configurator: amber
 * - Identificator: cyan
 * - Check Configuration: purple
 * - Firmware Update: orange
 */

import { ControlCodes } from '@bcube/shared/constants';
import { BadgeCheck, CheckCheck, FolderDown, Settings, Terminal, Wrench } from 'lucide-react';

import { GridList, type GridListAction } from '@/components/ui/grid-list';
import { usePermissions } from '@/hooks/use-permissions';

const toolActions: GridListAction[] = [
  {
    title: 'Auto Test',
    href: '/production/tools/auto-test',
    icon: Wrench,
    iconForeground: 'text-blue-700',
    iconBackground: 'bg-blue-50',
    description:
      'Execute testes automáticos para validar dispositivos durante o processo de produção.',
    code: ControlCodes.production_tool_auto_test,
  },
  {
    title: 'Configurador',
    href: '/production/tools/configurator',
    icon: Settings,
    iconForeground: 'text-amber-700',
    iconBackground: 'bg-amber-50',
    description:
      'Configure parâmetros e definições técnicas dos dispositivos na linha de montagem.',
    code: ControlCodes.production_tool_configurator,
  },
  {
    title: 'Identificador',
    href: '/production/tools/identificator',
    icon: BadgeCheck,
    iconForeground: 'text-cyan-700',
    iconBackground: 'bg-cyan-50',
    description: 'Grave identificações únicas nos dispositivos.',
    code: ControlCodes.production_tool_identifier,
  },
  {
    title: 'Checagem',
    href: '/production/tools/check-configuration',
    icon: CheckCheck,
    iconForeground: 'text-purple-700',
    iconBackground: 'bg-purple-50',
    description: 'Faça a checagem do perfil de configuração de equipamentos.',
  },
  {
    title: 'Gravador de Firmware',
    href: '/production/tools/firmware-update',
    icon: FolderDown,
    iconForeground: 'text-orange-700',
    iconBackground: 'bg-orange-50',
    description: 'Inicie o processo de gravação de firmware.',
  },
  {
    title: 'Terminal Serial',
    href: '/production/tools/commands',
    icon: Terminal,
    iconForeground: 'text-emerald-700',
    iconBackground: 'bg-emerald-50',
    description: 'Envie comandos livres e acompanhe as respostas do dispositivo em tempo real.',
  },
];

export default function ToolsIndexPage() {
  const { hasPermissionPrefix } = usePermissions();
  const visibleActions = toolActions.filter(
    (action) => !action.code || hasPermissionPrefix(action.code),
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div>
          <h1 className="text-base font-semibold leading-7 text-gray-900">
            Módulo Produção - Ferramentas
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Esse módulo é responsável pelas funcionalidades das ferramentas utilizadas pela
            Produção.
          </p>
        </div>
      </div>
      <div className="mt-10 flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <GridList actions={visibleActions} className="bg-transparent" />
      </div>
    </div>
  );
}

export { ToolsIndexPage };
